import { Box, Typography, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { t } from "@lingui/core/macro";
import LabeledTextField from "../../labeledTextField/labeledTextField";
import { TaxonomyListProps } from "../../../api/customObjectsTypes";
import { getLanguages } from "../../../api/customObjects";
import LabelTable from "../../labelTable";
import { GenericDataTable } from "../../genericDataTable";

export const ConverterConfigurations = ({
    taxonomyData,
    setTaxonomyData,
}: {
    taxonomyData: TaxonomyListProps | null;
    setTaxonomyData: (data: TaxonomyListProps) => void;
}) => {

    const [languages, setLanguages] = useState<string[]>([]);

    useEffect(() => {
        getLanguages().then((response) => {
            setLanguages(response);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    interface Icolumns {
        label: string;
        key: string;
        value: string | undefined;
        select?: boolean;
        options?: string[];
        isCheckBox?: boolean;
        disabled?: boolean;
        onchange?: boolean;
    }

    const ColumnItems: Icolumns[] = [
        { label: t`Is Reference Taxonomy:`, key: "isReferenceTaxonomy", value: taxonomyData?.isReferenceTaxonomy?.toString(), select: true, options: ["true", "false"] },
        { label: t`Use Segment Node For Dimensions:`, key: "useSegmentNodeForDimensions", value: taxonomyData?.useSegmentNodeForDimensions?.toString(), select: true, options: ["true", "false"] },
        { label: t`Use Reporting Package Format:`, key: "useReportingPackageFormat", value: taxonomyData?.useReportingPackageFormat?.toString(), select: true, options: ["true", "false"] },
        { label: t`Data Type Registry Version:`, key: "dataTypeRegistryVersion", value: taxonomyData?.dataTypeRegistryVersion, },
    ];

    const handleChange = (key: string, value: any) => {
        if (!taxonomyData) return;

        let parsedValue: any = value;

        if (value === "true" || value === "false") {
            parsedValue = value === "true";
        }

        const keys = key.split(".");
        const updatedData = { ...taxonomyData };

        let currentLevel = updatedData as any;
        for (let i = 0; i < keys.length - 1; i++) {
            if (!currentLevel[keys[i]]) {
                currentLevel[keys[i]] = {};
            }
            currentLevel = currentLevel[keys[i]];
        }

        currentLevel[keys[keys.length - 1]] = parsedValue;

        setTaxonomyData(updatedData);
    };


    return (
        <>
            <Box sx={{ pt: 2, pb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {t`Converter Configurations`}
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {ColumnItems.map((item) => (
                        <Box sx={{ flex: "1 1 49%" }} key={item.key}>
                            <LabeledTextField
                                label={item.label}
                                value={item.value}
                                disabled={item.disabled}
                                fieldType={item.select ? "select" : "text"}
                                options={item?.options || []}
                                onValueChange={(value) => handleChange(item.key, value)}
                            />
                        </Box>
                    ))}
                </Box>
                <Divider />
                {taxonomyData?.defaultDimensionRole && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {t`Default Dimension Role`}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <LabeledTextField
                                label={t`Href`}
                                value={taxonomyData?.defaultDimensionRole.roleHref}
                                fieldType="text"
                                onValueChange={(value) => handleChange("defaultDimensionRole.roleHref", value)}
                            />
                            <LabeledTextField
                                label={t`Uri`}
                                value={taxonomyData?.defaultDimensionRole.roleUri}
                                fieldType="text"
                                onValueChange={(value) => handleChange("defaultDimensionRole.roleUri", value)}
                            />
                        </Box>
                    </Box>
                )}
                {taxonomyData?.hypercubeForLineItemsWithoutDimensions && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {t`Hypercube For Line Items Without Dimensions`}
                        </Typography>
                        <Box>
                            <LabeledTextField
                                label={t`Role Uri`}
                                onValueChange={(value) => handleChange("hypercubeForLineItemsWithoutDimensions.roleUri", value)}
                                value={taxonomyData?.hypercubeForLineItemsWithoutDimensions.roleUri}
                                fieldType="text"
                            />
                            <LabeledTextField
                                label={t`Role Href`}
                                onValueChange={(value) => handleChange("hypercubeForLineItemsWithoutDimensions.roleHref", value)}
                                value={taxonomyData?.hypercubeForLineItemsWithoutDimensions.roleHref}
                                fieldType="text"
                            />
                            <LabeledTextField
                                label={t`Element Href`}
                                onValueChange={(value) => handleChange("hypercubeForLineItemsWithoutDimensions.elementHref", value)}
                                value={taxonomyData?.hypercubeForLineItemsWithoutDimensions.elementHref}
                                fieldType="text"
                            />
                            <LabeledTextField
                                label={t`Placeholder element id`}
                                onValueChange={(value) => handleChange("hypercubeForLineItemsWithoutDimensions.placeholderElementId", value)}
                                value={taxonomyData?.hypercubeForLineItemsWithoutDimensions.placeholderElementId}
                                fieldType="text"
                            />
                        </Box>
                    </Box>
                )}
                {taxonomyData?.additionalEntrypoints && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {t`Additional Entry Points`}
                        </Typography>
                        <GenericDataTable
                            header={t`Entry Point`}
                            data={taxonomyData?.additionalEntrypoints || []}
                            setLabels={
                                (updatedLabels) => {
                                    if (!taxonomyData) return;

                                    const updatedData = { ...taxonomyData, additionalEntrypoints: updatedLabels as string[] };
                                    setTaxonomyData(updatedData);
                                }
                            }
                        />
                    </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t`Labels`}
                    </Typography>
                    <LabelTable
                        data={taxonomyData?.labels || []}
                        languages={languages}
                        setData={
                            (updatedLabels) => {
                                if (!taxonomyData) return;

                                const updatedData = { ...taxonomyData, labels: updatedLabels };
                                setTaxonomyData(updatedData);
                            }
                        }
                    />
                </Box>
            </Box>

        </>
    );
};
